import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { S3 } from "../../../../../constants";
import useWindowSize from "../../../../../hooks/useWindowSize";
import Modal from "../../../../../parts/modal-new";
import AlignHint from "../../../../../parts/photo-compare/AlignHint";
import ControlGroup from "../../../../../parts/photo-compare/ControlGroup";
import ModeTabs from "../../../../../parts/photo-compare/ModeTabs";
import PairCompareControls from "../../../../../parts/photo-compare/PairCompareControls";
import PairCompareStage from "../../../../../parts/photo-compare/PairCompareStage";
import usePairCompareState from "../../../../../parts/photo-compare/usePairCompareState";
import SvgIcon from "../../../../../parts/SvgIcon";
import Photo from "./Photo";
import StatInformation from "./StatInformation";

const CompareListPhotosModal = ({
  selectedPhotos,
  setSelectedPhotos,
  closeModal,
  closeModalAndReset,
  sides,
  compareOnlyTwo,
}) => {
  const { t } = useTranslation("athlete-portal");
  const changePicture = (photo) => {
    setSelectedPhotos((prevSelectedPhotos) => {
      return prevSelectedPhotos.filter(
        (selectedPhoto) => selectedPhoto !== photo
      );
    });
    closeModal();
  };
  const windowSize = useWindowSize();

  const [zoomView, setZoomView] = useState(null);
  const [mode, setMode] = useState("sbs");

  // Side by side can render a missing photo as an empty slot, so the angle
  // picker offers anything either date has. Slider and fade need a real pair.
  const viewableAngles = compareOnlyTwo
    ? []
    : sides.filter(
        (side) =>
          selectedPhotos[0].bodyPhotos[side] ||
          selectedPhotos[1].bodyPhotos[side]
      );
  const isPairable = (side) =>
    Boolean(
      selectedPhotos[0].bodyPhotos[side] && selectedPhotos[1].bodyPhotos[side]
    );

  const [angle, setAngle] = useState(
    viewableAngles.find(isPairable) || viewableAngles[0]
  );

  // Comparing two hand-picked photos is always a pair; comparing two dates
  // depends on whether the chosen angle exists on both of them.
  const canPair = compareOnlyTwo || isPairable(angle);
  const disabledModes = canPair ? [] : ["slider", "fade"];
  // Guard against landing on slider/fade for an angle that has no pair.
  const activeMode = canPair ? mode : "sbs";

  // Zooming in (per-angle drill-in, or the two-photo view's single Zoom
  // button) is a sub-state of side by side; the controls step aside while
  // it is open so the photo(s) get the space back.
  const inDrilldown = Boolean(zoomView);

  const compareState = usePairCompareState(angle || "pair");

  const photoFor = (index) =>
    selectedPhotos[index].bodyPhotos[compareOnlyTwo ? sides[index] : angle];

  // Dates are what tells the two apart here. In the two-photo flow they can
  // also be different angles, so name the angle too.
  const labelFor = (index) => {
    const date = moment(selectedPhotos[index].utcDate, "YYYYMMDD").format("ll");
    return compareOnlyTwo
      ? `${date} · ${t(`questionProgress.${sides[index]}`)}`
      : date;
  };

  const pairSide = (index, controlLabel) => ({
    src: S3.toCDN(photoFor(index).s3Path),
    label: labelFor(index),
    // Dates are too long for the toolbar captions, so it gets a short form.
    controlLabel,
  });
  const left = canPair ? pairSide(0, t("questionProgress.previous")) : null;
  const right = canPair ? pairSide(1, t("questionProgress.current")) : null;

  // Side by side's own "zoom in" control: it lives in the top control row,
  // next to the View panel, instead of sitting between the photos. It hides
  // itself once zoomed, the same way it used to.
  const showSbsZoom =
    activeMode === "sbs" && !zoomView && (compareOnlyTwo || Boolean(angle));
  const handleSbsZoom = () => setZoomView(compareOnlyTwo ? true : angle);

  return (
    <Modal
      title={t("progressPhotos.comparePhotos")}
      className={"compare-list-ph photo-compare"}
      onClose={closeModalAndReset}
      dontRefreshOnClose={true}
    >
      <div className="body">
        <header>
          {!zoomView ? (
            <>
              <hgroup>
                <div>
                  <p className="s1">
                    {moment(selectedPhotos[0].utcDate, "YYYYMMDD").format("LL")}
                  </p>
                  <StatInformation
                    stat={selectedPhotos[0]}
                    column={windowSize.innerWidth < 960}
                  />
                </div>
                <button
                  type="button"
                  data-button-type="primary"
                  onClick={() => changePicture(selectedPhotos[0])}
                >
                  {t("progressPhotos.change")}
                </button>
              </hgroup>
              <hgroup>
                <div>
                  <p className="s1">
                    {moment(selectedPhotos[1].utcDate, "YYYYMMDD").format("LL")}
                  </p>

                  <StatInformation
                    stat={selectedPhotos[1]}
                    column={windowSize.innerWidth < 960}
                  />
                </div>
                <button
                  type="button"
                  data-button-type="primary"
                  onClick={() => changePicture(selectedPhotos[1])}
                >
                  {t("progressPhotos.change")}
                </button>
              </hgroup>
            </>
          ) : (
            <div className="flex gap-2 items-center">
              <SvgIcon
                src={"/img/new-icons/back-button.svg"}
                onClick={() => setZoomView(null)}
              />
              {!compareOnlyTwo && (
                <span>{capitalizeFirstLetter(zoomView)}</span>
              )}
            </div>
          )}
        </header>

        {!inDrilldown && (
          <div className="compare-controls">
            {/* Balances the hint at the far right so the panels between them
                stay centred in the row instead of being pushed left. */}
            {activeMode !== "sbs" && (
              <span className="compare-controls-spacer" aria-hidden="true" />
            )}
            <ModeTabs
              mode={activeMode}
              onChange={setMode}
              disabledModes={disabledModes}
            />
            {viewableAngles.length > 1 && (
              <ControlGroup panel caption={t("questionProgress.view")}>
                <div className="angle-tabs">
                  {viewableAngles.map((a) => (
                    <button
                      key={a}
                      type="button"
                      className={a === angle ? "active" : ""}
                      onClick={() => setAngle(a)}
                    >
                      {/* The label wears its own element so the selected
                          segment can carry both a chip background and gradient
                          lettering — see the CSS. */}
                      <span className="compare-segment-label">
                        {t(`questionProgress.${a}`)}
                      </span>
                    </button>
                  ))}
                </div>
              </ControlGroup>
            )}
            {showSbsZoom && (
              // "Zoom" reads fine unlocalized — it's a universal enough term
              // not to need a translation key of its own. The pill housing
              // reuses the same classes as the previous/current zoom
              // steppers below, so the button matches them exactly.
              <ControlGroup panel caption="Zoom">
                <span className="compare-zoom-stepper">
                  <span className="compare-zoom-buttons">
                    <button
                      type="button"
                      onClick={handleSbsZoom}
                      aria-label={t("questionProgress.expand")}
                    >
                      <SvgIcon
                        src="/img/new-icons/Zoom.svg"
                        useCurrentColor
                        style={{ width: "1rem", height: "1rem" }}
                      />
                    </button>
                  </span>
                </span>
              </ControlGroup>
            )}
            {activeMode !== "sbs" && (
              <>
                <PairCompareControls
                  stacked
                  mode={activeMode}
                  left={left}
                  right={right}
                  state={compareState}
                />
                <span className="compare-controls-end">
                  <AlignHint pill />
                </span>
              </>
            )}
          </div>
        )}

        {activeMode !== "sbs" ? (
          <PairCompareStage
            mode={activeMode}
            left={left}
            right={right}
            state={compareState}
          />
        ) : compareOnlyTwo ? (
          <CompareTwo
            sides={sides}
            selectedPhotos={selectedPhotos}
            zoomView={zoomView}
          />
        ) : (
          <CompareAll
            angle={angle}
            selectedPhotos={selectedPhotos}
            zoomView={zoomView}
          />
        )}
      </div>
    </Modal>
  );
};
function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
const CompareAll = ({ angle, selectedPhotos, zoomView }) => {
  if (!angle) return null;
  if (zoomView)
    return (
      <section>
        <div className="flex gap-4">
          <Photo
            src={
              selectedPhotos[0].bodyPhotos[zoomView]
                ? selectedPhotos[0].bodyPhotos[zoomView].s3Path
                : null
            }
            className="progress-photo"
          />

          <Photo
            src={
              selectedPhotos[1].bodyPhotos[zoomView]
                ? selectedPhotos[1].bodyPhotos[zoomView].s3Path
                : null
            }
            className="progress-photo"
          />
        </div>
      </section>
    );
  return (
    <article>
      <Photo
        src={
          selectedPhotos[0].bodyPhotos[angle]
            ? selectedPhotos[0].bodyPhotos[angle].s3Path
            : null
        }
        className="progress-photo"
      />
      <Photo
        src={
          selectedPhotos[1].bodyPhotos[angle]
            ? selectedPhotos[1].bodyPhotos[angle].s3Path
            : null
        }
        className="progress-photo"
      />
    </article>
  );
};
const CompareTwo = ({ sides, selectedPhotos, zoomView }) => {
  // Mirrors CompareAll's zoomed layout: swapping to the section/div markup
  // hands the photos to the width-driven `.body > section > div > img` rule
  // instead of the height-capped `.body > article > img` one, so zoom is an
  // actual size increase and not just a decluttered article.
  if (zoomView)
    return (
      <section>
        <div className="flex gap-4">
          <Photo
            src={
              selectedPhotos[0].bodyPhotos[sides[0]]
                ? selectedPhotos[0].bodyPhotos[sides[0]].s3Path
                : null
            }
            className="progress-photo"
          />
          <Photo
            src={
              selectedPhotos[1].bodyPhotos[sides[1]]
                ? selectedPhotos[1].bodyPhotos[sides[1]].s3Path
                : null
            }
            className="progress-photo"
          />
        </div>
      </section>
    );
  return (
    <article>
      <Photo
        src={
          selectedPhotos[0].bodyPhotos[sides[0]]
            ? selectedPhotos[0].bodyPhotos[sides[0]].s3Path
            : null
        }
        className="progress-photo"
      />
      <Photo
        src={
          selectedPhotos[1].bodyPhotos[sides[1]]
            ? selectedPhotos[1].bodyPhotos[sides[1]].s3Path
            : null
        }
        className="progress-photo"
      />
    </article>
  );
};
export default CompareListPhotosModal;
